import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  styled,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import moment from "moment";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useDrop } from "react-dnd";
import toast from "react-hot-toast";
import demoStoreIcon from "../../../assets/Images/man.png";
import { DateTimeFormatTZ } from "../../../constants/time-picker-constant";
import { useStore } from "../../../contexts/store-provider";
import {
  getInitialAndLastDates,
  getIsCellDisabled,
  isDateInPublishedShiftRange,
} from "../../../helpers/date-time-helper";
import { IShiftSchedule } from "../../../models/response/IShiftManagementResponse";
import FreeShiftPopupHOC from "../../../shared-components/hoc/free-shift-popup-hoc";
import DropdownInput from "../../../shared-components/input/DropdownInput";
import ConfirmPopup from "../../../shared-components/popup/confirm/confirm-popup";
import ConfirmPopupHOC from "../../../shared-components/popup/confirm/confirm-popup-hoc";
import DeletePopup from "../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../shared-components/popup/delete/delete-popup-hoc";
import ShowPopupHOC from "../../../shared-components/popup/show/show-popup-hoc";
import { formatMessage } from "../../../translations/format-message";
import ChangeShiftPopupHOC from "../../auth/hoc/change-shift-popup-hoc";
import PublishSchedulePopupHOC from "../../auth/hoc/publish-schedule-popup-hoc";
import ChangeShiftForm from "../forms/change-shift-form";
import FreeShiftForm from "../forms/free-shift-form";
import PublishForm from "../forms/publish-form";
import DraggableShift from "./draggable-shift";
import {
  getAllDatesOfMonth,
  getDateCopyDetails,
  getJapaneseDay,
} from "./lib/utils";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import ConfirmEmailPopupHOC from "../../../shared-components/popup/confirm/confirm-email-popup-hoc";
import { FormattedMessage } from "react-intl";
import { isSameDay } from "date-fns";
import { DATE_FORMAT, IsItInHoliday, IsStoreClosedByDate, IsStoreClosedByDateForShift, JapaneseHolidays } from "../../../constants/constants";
//  import MoreVertIcon from '@mui/icons-material/MoreVert';

type StaffMember = {
  id: string;
  name: string;
  role: string;
  avatar: string;
};

type ScheduleCellProps = {
  staffId: number;
  date: Date;
  shift: any | null;
  onDrop: (newShift: IShiftSchedule) => void;
  onEdit: (shift: number) => void;
  onRemove: (shift: IShiftSchedule, date: Date) => void;
  onChange: (shift: IShiftSchedule, date: Date, staffId: number) => void;
  isDisabled: boolean;
  isPermissionViewOnly: boolean;
  onCellClick: (staffId: number, date: Date) => void;
  isPublisedDate: boolean;
  getClosedDay: { WeekDays: number; IsClosed: boolean }[];
};

type ScheduleGridProps = {
  storeId: number;
  showPublishSchedulePopup: boolean;
  publishSchedulePopupToggleHandler: () => void;
  addToggleHandler: () => void;
  setSelectedShift: (id: number) => void;
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
  dates: Date[];
  setDates: React.Dispatch<React.SetStateAction<Date[]>>;
  showConfirmPopup: boolean;
  confirmPopupToggleHandler: () => void;
  showFreeShiftPopup: boolean;
  freeShiftPopupToggleHandler: () => void;
  showChangeShiftPopup: boolean;
  changeShiftPopupToggleHandler: () => void;
  isDisabled: boolean;
  showShowPopup: boolean;
  showPopupToggleHandler: () => void;
  showConfirmEmailPopup: boolean;
  confirmEmailPopupToggleHandler: () => void;
  showInvalidShifts: boolean;
};

const StyledCell = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isOver" && prop !== "disabled",
})<{
  isOver: boolean;
  disabled: boolean;
}>(({ theme, isOver, disabled }) => ({
  height: 96,
  padding: theme.spacing(1),
  minWidth: "130px",
  backgroundColor: disabled
    ? theme.palette.action.disabledBackground
    : isOver
    ? theme.palette.action.hover
    : theme.palette.background.paper,
  cursor: disabled ? "not-allowed" : "default",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
}));

const StyledHeaderCell = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  minWidth: "130px",
  height: 72,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const StyledStaffCell = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  height: 96,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderRight: `1px solid ${theme.palette.divider}`,
}));

// ScheduleCell Component
const ScheduleCell = ({
  staffId,
  date,
  shift,
  onDrop,
  onEdit,
  onRemove,
  onChange,
  isDisabled,
  onCellClick,
  isPermissionViewOnly,
  isPublisedDate,
  getClosedDay,
}: ScheduleCellProps) => {
  const isDisabledRef = React.useRef(isDisabled);
  const isPublishedDateRef = React.useRef(isPublisedDate);
  const shiftRef = React.useRef(shift);
  const { masterManagementStore } = useStore();
  const { getReservationSettingDetail } = masterManagementStore;

  // Update both refs whenever their values change
  React.useEffect(() => {
    isDisabledRef.current = isDisabled;
    isPublishedDateRef.current = isPublisedDate;
    shiftRef.current = shift;
  }, [isDisabled, isPublisedDate, shift]);

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: "SHIFT",

      // Check conditions using the latest state values (not stale closures)
      canDrop: () => {
        // Check the current state, not the closure values
        return !isDisabled && !isPublisedDate;
      },

      drop: (item: IShiftSchedule) => {
        // Double-check conditions at drop time
        if ((!isDisabled || !isPermissionViewOnly) && !isPublisedDate) {
          onDrop(item);
        }
      },

      collect: (monitor) => ({
        isOver: monitor.isOver() && !isDisabled,
      }),
    }),
    [isDisabled, isPermissionViewOnly, isPublisedDate]
  ); // Add dependencies to recreate the drop target when these change

  const blockDate = date;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  blockDate.setHours(0, 0, 0, 0);

  const isDropping = isPublisedDate
              ? isDisabled &&
                IsStoreClosedByDate(getReservationSettingDetail, moment(date).format('YYYY-MM-DD'))
                ? false
                : false
              : isDisabled
              ? IsStoreClosedByDate(getReservationSettingDetail, moment(date).format('YYYY-MM-DD'))
                ? false
                : false
              : true;

  return (
    <div
      ref={drop}
      className="w-100 h-100 d-flex align-items-center justify-content-center"
      onClick={() =>
        !isDisabled && !shift && !isPublisedDate && onCellClick(staffId, date)
      }
    >
      {shift ? (
        shift.Id !== -1 ? (
          // Existing shift - still clickable
          <DraggableShift
            shift={shift}
            isScheduled={true}
            onChange={() => onChange(shift, date, staffId)}
            onRemove={() => onRemove(shift, date)}
            isDisabled={isDisabled || isPermissionViewOnly}
            isSchedulePublished={shift.ScheduleType == 1 ? true : false}
          />
        ) : (
          <div
            className="bg-gray position-relative w-100 h-100 d-flex align-items-center justify-content-center"
          >
            <div
              style={{
                position: "absolute",
                top: "4px",
                right: "4px",
              }}
            >
              {(!isPermissionViewOnly && shift.ScheduleType != 1 && !isDisabled)  && (
                <FreeShiftMenuDropdown
                  shiftId={shift.ShiftId}
                  onRemove={() => onRemove(shift, date)}
                  date={date}
                  shift={shift}
                />
              )}
            </div>
            <Typography className="text-center" color="text.secondary">
              <span className="shift_start_time">{moment(shift.StartTime, "HH:mm:ss").format("HH:mm")}</span>&nbsp;
              <span className="shift_time_seperator">-</span>&nbsp;
              <span className="shift_end_time">{moment(shift.EndTime, "HH:mm:ss").format("HH:mm")}</span>
            </Typography>
          </div>
        )
      ) : (
        // Empty cell - droppable
        <div
          className={`${(isOver && isDropping) && 'bg-primary'} w-100 h-100 d-flex align-items-center justify-content-center`}
        >
        <Typography color="text.secondary">
          {(() => {
            const isClosed =
              IsStoreClosedByDate(getReservationSettingDetail, moment(date).format("YYYY-MM-DD"));

            if (isPublisedDate) {
              if (isDisabled && isClosed) {
                return formatMessage("holiday");
              } else {
                return formatMessage("published");
              }
            } else {
              if (isDisabled) {
                if (isClosed) {
                  return formatMessage("holiday");
                } else {
                  return formatMessage("shift_not_assigned");
                }
              } else {
                return formatMessage("drop_shift_here");
              }
            }
          })()}
        </Typography>
        </div>
      )}
    </div>
  );
};

// ScheduleGrid Component
const ScheduleGrid = (props: ScheduleGridProps) => {
  const { masterManagementStore, shiftManagementStore } = useStore();
  const {
    dates,
    setDates,
    storeId,
    isDisabled,
    showPopupToggleHandler,
    showShowPopup,
  } = props;
  const { getReservationSettingDetail } = masterManagementStore;
  const {
    getShiftScheduleList,
    getShiftScheduleDateRange,
    GetAllShiftScheduleListByStoreIdService,
    AddShiftScheduleService,
    PublishShiftScheduleService,
    UnPublishShiftScheduleService,
    DeleteScheduleService,
    CopyShiftScheduleService,
    addShiftScheduleListState,
    resetAddShiftScheduleListState,
    resetCopyScheduleListState,
    copyScheduleListState,
    publishScheduleListState,
    resetPublishScheduleListState,
    deleteShiftScheduleState,
    resetDeleteShiftScheduleState,
    addUpdateShiftManagementState,
    getClosedDays,
    deleteShiftManagementState,
    OverWriteShiftService,
    overWriteShiftScheduleListState,
    resetOverWriteShiftScheduleListState,
    AddFreeShiftScheduleService,
    resetFreeAddShiftScheduleListState,
    addFreeShiftScheduleListState,
    allShiftOption,
    changeShiftScheduleListState,
    resetChangeShiftScheduleListState,
    ChangeShiftService,
    getShiftScheduleListState,
    getAllShiftStaffOptions
  } = shiftManagementStore;
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [publishDateRange, setPublishedDateRange] = useState<{
    initialDate: number;
    lastDate: number;
    initialDateObj: Date | null;
    lastDateObj: Date | null;
  }>({ initialDate: 0, lastDate: 0, initialDateObj: null, lastDateObj: null });
  const [shifts, setShifts] = useState<IShiftSchedule[]>([
    ...getShiftScheduleList,
  ]);
  const [selectedShiftChange, setSelectedShiftChange] = useState<{
    Shift: number;
    Date: Date;
    StaffId: number;
  } | null>(null);
  const [freeShift, setFreeShift] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [changeShiftId, setChangeShiftId] = useState<number>(-1);
  const [getClosedDay, setGetClosedDay] = useState([...getClosedDays]);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [droppedShift, setDroppedShift] = useState<{
    shift: IShiftSchedule;
    staffId: number;
    date: string;
    existingShift?: IShiftSchedule | null;
  } | null>(null);
  const [assignmentType, setAssignmentType] = useState("selected");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [allocation, setAllocation] = useState(2);
  const [currentAllocationClassName, setAllocationClassName] =
    useState("bi_weekly");
  const [selectedOption, setSelectedOption] = useState<number>(-1);
  const [overWriteShifts, setOverWriteShifts] = useState<{
    NewShifts: IShiftSchedule[];
    OldShifts: IShiftSchedule[];
  }>();
  const [scheduleId, setScheduleId] = useState<number>(-1);
  const [submitPublishValues, setSubmitPublishValues] = useState<any>(null);
  // const [dates, setDates] = useState<Date[]>([]);

  useEffect(() => {
    if (getClosedDays.length > 0) {
      setGetClosedDay([...getClosedDays]);
    } else {
      setGetClosedDay([]);
    }
  }, [getClosedDays, setGetClosedDay]);

  const callScheduleList = useCallback(() => {
    const startDate = new Date(dates[0]);
    const endDate = new Date(dates[dates.length - 1]);
    if (dates[0] && dates[dates.length - 1] && endDate > startDate) {
      GetAllShiftScheduleListByStoreIdService(
        storeId,
        dates[0],
        dates[dates.length - 1]
      );
    }
  }, [GetAllShiftScheduleListByStoreIdService, dates, storeId]);

  useEffect(() => {
    // Call the schedule list on initial render or when dates change
    callScheduleList();
  }, [callScheduleList]);

  useEffect(() => {
    // Handle side effects after state updates
    if (addShiftScheduleListState.success) {
      toast.success(formatMessage("shift_assigned_successfully"));
      callScheduleList();
      resetAddShiftScheduleListState();
    }

    if (addFreeShiftScheduleListState.success) {
      toast.success(formatMessage("shift_assigned_successfully"));
      callScheduleList();
      props.freeShiftPopupToggleHandler();
      resetFreeAddShiftScheduleListState();
    }

    if (overWriteShiftScheduleListState.success) {
      toast.success(formatMessage("shift_assigned_successfully"));
      callScheduleList();
      props.confirmPopupToggleHandler();
      resetOverWriteShiftScheduleListState();
    }

    if (changeShiftScheduleListState.success) {
      toast.success(formatMessage("shift_assigned_successfully"));
      callScheduleList();
      props.changeShiftPopupToggleHandler();
      resetChangeShiftScheduleListState();
    }

    if (deleteShiftScheduleState.success) {
      toast.success(formatMessage("shift_unassigned_successfully"));
      callScheduleList();
      props.deletePopupToggleHandler();
      resetDeleteShiftScheduleState();
    }

    if (copyScheduleListState.success) {
      toast.success(formatMessage("shift_copied_successfully"));
      callScheduleList();
      resetCopyScheduleListState();
    }

    if (publishScheduleListState.success) {
      if (selectedOption === 1) {
        if(checkIfPublishDateHaveExtraDates(getShiftScheduleDateRange, {...submitPublishValues})){
          let message = formatMessage("it_has_been_made_public_up_to_the_{date}_for_which_shifts_have_been_set");
          toast.success(message.replace("{date}", moment(getShiftScheduleDateRange.lastDate).format(DATE_FORMAT)));
        }
        else
          toast.success(formatMessage("shift_published_successfully"));
        setSubmitPublishValues(null)
      }
      if (selectedOption === 2) {
        toast.success(formatMessage("shift_unpublished_successfully"));
      }
      callScheduleList();
      // props.publishSchedulePopupToggleHandler();
      resetPublishScheduleListState();
    }

    if (addUpdateShiftManagementState.success) {
      // toast.success(formatMessage("shift_assigned_successfully"));
      callScheduleList();
      // resetAddUpdateShiftManagementState();
    }

    if (deleteShiftManagementState.success) {
      // toast.success(formatMessage("shift_assigned_successfully"));
      callScheduleList();
      // resetAddUpdateShiftManagementState();
    }
  }, [
    addShiftScheduleListState.success,
    deleteShiftManagementState.success,
    addFreeShiftScheduleListState.success,
    overWriteShiftScheduleListState.success,
    deleteShiftScheduleState.success,
    copyScheduleListState.success,
    publishScheduleListState.success,
    selectedOption,
    props,
    callScheduleList,
    resetAddShiftScheduleListState,
    resetFreeAddShiftScheduleListState,
    resetOverWriteShiftScheduleListState,
    resetDeleteShiftScheduleState,
    resetCopyScheduleListState,
    resetPublishScheduleListState,
    addUpdateShiftManagementState.success,
    changeShiftScheduleListState.success,
    resetChangeShiftScheduleListState,
  ]);

  useEffect(() => {
    setShifts([...getShiftScheduleList]);
    setPublishedDateRange(
      getInitialAndLastDates(getShiftScheduleList, getClosedDays, currentDate)
    );
  }, [getShiftScheduleList]);

  useEffect(() => {
    // setDates(
    //   allocation === 3
    //     ? getAllDatesOfMonth(currentDate) //getDatesTillEndOfMonth(currentDate)
    //     : allocation === 2
    //     ? getDatesForTwoWeek(currentDate)
    //     : getDatesForOneWeek(currentDate)
    // );
    setDates(getAllDatesOfMonth(currentDate));
  }, [currentDate]);

  const year = moment(currentDate).format("YYYY");

  const monthName = moment(currentDate).format("MMMM");

  const allocationList: any[] = [
    { id: "1", value: formatMessage("weekly") },
    { id: "2", value: formatMessage("bi_weekly") },
    { id: "3", value: formatMessage("monthly") },
  ];
  // Handle Menu opening
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAllocationChange = (event: any) => {
    const val = Number(event.target.value);
    setAllocation(val);
    const className =
    val === 1 ? 'weekly' : val === 2 ? 'bi_weekly' : 'monthly';

    setTimeout(() => {
      setAllocationClassName(className);
    }, 10);
  
  };

  // Handle Menu closing
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle option selection
  const handleOptionSelect = (option: number) => {
    setSelectedOption(option);
    handleMenuClose(); // Close the menu
  };

  const handlePublish = () => {
    props.publishSchedulePopupToggleHandler();
    showPopupToggleHandler();
    let values = { ...submitPublishValues };
    if (selectedOption === 1) {
      PublishShiftScheduleService({ ...values });
    } else if (selectedOption === 2) {
      UnPublishShiftScheduleService({ ...values });
    }
  };

  const handlePublishConfirmPopupHandler = (values: any) => {
    // if(checkIfPublishDateHaveExtraDates(getShiftScheduleDateRange, {...values}))
    //   toast.error(formatMessage("user_cannot_publish_shift_byond_the_last_asigned_shift_date"));
    // else{
      setSubmitPublishValues({ ...values, StoreId: storeId });
      showPopupToggleHandler();
    // }
  };
  const handleShiftChange = (values: any) => {
    let data = {
      ShiftId: values.Shift.ShiftId,
      StaffId: selectedShiftChange?.StaffId,
      StoreId: storeId,
      ScheduleType: 0,
      Date: selectedShiftChange?.Date,
      FreeStartTime: values.Shift.FreeStartTime,
      FreeEndTime: values.Shift.FreeEndTime,
    };
    ChangeShiftService({
      NewShifts: [{ ...data }],
      OldShifts: [{ Id: selectedShiftChange?.Shift }],
    });
  };

  const handleFreeTimeShift = (values: any) => {
    let data = {
      Id: -1,
      StaffId: freeShift.StaffId,
      StoreId: storeId,
      ScheduleType: 0,
      Date: freeShift.Date,
      FreeStartTime: values.FreeStartTime,
      FreeEndTime: values.FreeEndTime,
    };
    AddFreeShiftScheduleService([{ ...data }]);
  };

  // Handle switching months
  const handleMonthChange = (amount: number) => {
    const newDate = new Date(currentDate);
    const day = newDate.getDate();

    // Move to first of the month first (avoids rollover)
    newDate.setDate(1);
    newDate.setMonth(newDate.getMonth() + amount);

    // Set back to original day or last valid day of the new month
    const lastDayOfNewMonth = new Date(
      newDate.getFullYear(),
      newDate.getMonth() + 1,
      0
    ).getDate();

    newDate.setDate(Math.min(day, lastDayOfNewMonth));
    setCurrentDate(newDate);
  };

  // Handle switching weeks
  const handleDateChange = (amount: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + amount * 7);
    setCurrentDate(newDate);
  };

  const isCellDisabled = useCallback(
    (
      date: Date,
      initialDatePublished: Date | null,
      lastDatePublished: Date | null
    ) => {
      // Debug logging
      // Handle case where data isn't loaded yet
      if (!Array.isArray(getClosedDay) || getClosedDay.length === 0) {
        // Only disable past dates if closed days not loaded yet
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
      }

      // Normal checks
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const isPastDate = date < today;

      const dayOfWeek = date.getDay();
      const isClosed = getClosedDay.some((closedDay) => {
        return closedDay.WeekDays === dayOfWeek;
      });

      if(IsItInHoliday(moment(date).format("YYYY-MM-DD")) && isClosed){
         return isPastDate || IsStoreClosedByDate(getReservationSettingDetail, moment(date).format("YYYY-MM-DD"));
      }
      else{
        return isClosed || isPastDate || IsStoreClosedByDate(getReservationSettingDetail, moment(date).format("YYYY-MM-DD"));
      }
    },
    [getClosedDay]
  );

  const getShiftForCell = (
    staffId: number,
    date: Date
  ): IShiftSchedule | null => {
    const shift = shifts.find(
      (shift) =>
        shift.StaffId === staffId &&
        new Date(shift.Date).toDateString() === date.toDateString()
    );
    return shift
      ? {
          ...shift,
          Id: shift.ShiftId,
          ShiftId: shift.ShiftId,
          ScheduleId: shift.Id,
          ShiftStatus: shift.ShiftStatus,
        }
      : null;
  };

  const handleCellClick = (staffId: number, date: Date) => {
    setFreeShift({ StaffId: staffId, Date: date });
    props.freeShiftPopupToggleHandler();
  };

  const handleEditShift = (shiftId: number) => {
    props.addToggleHandler();
    props.setSelectedShift(shiftId);
  };

  const handleDeleteShift = (shift: IShiftSchedule, date: Date) => {
    setScheduleId(shift.ScheduleId ?? shift.Id);
    props.deletePopupToggleHandler();
  };
  const handleChangeShift = (
    shift: IShiftSchedule,
    date: Date,
    staffId: number
  ) => {
    if(props.showInvalidShifts){
      toast.error(formatMessage("can't_change_invalid_shift"))
    }
    else{
      setScheduleId(shift.ScheduleId ?? shift.Id);
      setSelectedShiftChange({
        Shift: shift.ScheduleId ?? shift.Id,
        Date: date,
        StaffId: staffId,
      });
      props.changeShiftPopupToggleHandler();
      setChangeShiftId(shift.ShiftId);
    }
  };

  const removeShiftHandler = () => {
    DeleteScheduleService(scheduleId);
  };

  const overWriteShiftHandler = () => {
    OverWriteShiftService(overWriteShifts);
  };

  const onSubmitUnPublishHandler = () => {
    UnPublishShiftScheduleService({
      StoreId: storeId,
      StartDate: dates[0],
      EndDate: dates[dates.length - 1],
    });
    props.confirmEmailPopupToggleHandler();
  };

  const handleCopySchedule = () => {
    const { previousMonth, currentMonth, previousYear, currentYear } =
      getDateCopyDetails(currentDate);

    CopyShiftScheduleService({
      StoreId: storeId,
      PrevMonth: previousMonth,
      PrevYear: previousYear,
      NewMonth: currentMonth,
      NewYear: currentYear,
    });
  };

  const handleAssignmentConfirm = () => {
    if (droppedShift) {
      const { shift, staffId, date } = droppedShift;
      const publishedShiftsDateRange = getInitialAndLastDates(getShiftScheduleList, getClosedDays, new Date(date));

      const dateIsInPast = isDateInPublishedShiftRange(publishedShiftsDateRange, new Date(date));
      if(dateIsInPast){
        toast.error(formatMessage("future_publish_date_present"));
        setIsAssignmentModalOpen(false);
        setDroppedShift(null);
        setAssignmentType("selected");
        return;
      }

      const newShift = { ...shift, StaffId: staffId, Date: date };

      const IsCommonDates = (
        oldShifts: IShiftSchedule[],
        newShift: IShiftSchedule[]
      ) => {
        let oldDates = oldShifts
          .filter((shift) => shift.StaffId === droppedShift.staffId)
          .map((shift) => moment(shift.Date).format("YYYY-MM-DD"));
        let newDates = newShift
          .filter((shift) => shift.StaffId === droppedShift.staffId)
          .map((shift) => moment(shift.Date).format("YYYY-MM-DD"));
        const set1 = new Set(oldDates);
        let commonDates = newDates.filter((date: string) => set1.has(date));
        return commonDates;
      };

      const getCompleteWeekForDate = (
        allDates: Date[],
        selectedDate: string | number | Date
      ) => {
        // Convert to date object if it's not already
        const targetDate = new Date(selectedDate);

        // Get the day of the week (0 = Sunday, 1 = Monday, etc.)
        const dayOfWeek =
          targetDate.getDay() === 0 ? 6 : targetDate.getDay() - 1;

        // Calculate the date of the Sunday that starts this week
        // by subtracting the current day number from the selected date
        const startOfWeek = new Date(targetDate);
        startOfWeek.setDate(targetDate.getDate() - dayOfWeek);

        // Create an array to hold the week's dates
        const weekDates = [];

        // Fill the array with the 7 days of the week
        for (let i = 0; i < 7; i++) {
          const currentDate = new Date(startOfWeek);
          currentDate.setDate(startOfWeek.getDate() + i);
          weekDates.push(currentDate);
        }

        return weekDates;
      };

      let updatedShifts = [];

      // Helper function to check if a date is closed
      const isClosedDay = (dateToCheck: number) => {
        // Convert the date to a string format that matches closedDays array format
        // Assuming closedDays is an array of date strings - adjust this formatting as needed
        const formattedDate = moment(dateToCheck).format("YYYY-MM-DD");
        let isClosed = getClosedDay
          .map((day) => day.WeekDays)
          .includes(dateToCheck);

        if (IsItInHoliday(formattedDate) && isClosed) {
          return false;
        } else {
          return isClosed;
        }
      };

      const selectedDateIndex = dates.findIndex(
        (d) =>
          moment(d).format("YYYY-MM-DD") === moment(date).format("YYYY-MM-DD")
      );

      // If the selected date is not found, break
      switch (assignmentType) {
        case "daily":
          if (selectedDateIndex === -1) break;
          // Assign shift to alternate weekdays starting from the selected date
          for (let i = selectedDateIndex; i < dates.length; i++) {
            const currentDate = new Date(dates[i]);
            // Only add weekdays (not Saturday or Sunday) and not closed days
            if (!(isClosedDay(currentDate.getDay()) || IsStoreClosedByDateForShift(getReservationSettingDetail, currentDate))) {
              updatedShifts.push({
                ...newShift,
                Date: moment(currentDate).format(DateTimeFormatTZ),
              });
            }
          }
          break;
        case "this_week":
          // If the selected date is not found, break
          if (selectedDateIndex === -1) break;

          // Usage:
          const currentDay = new Date(dates[selectedDateIndex]);
          let weekDates = getCompleteWeekForDate(dates, currentDay);
          // Assign shift to alternate weekdays starting from the selected date
          for (let i = 0; i < weekDates.length; i++) {
            const currentDate = new Date(weekDates[i]);
            // Only add weekdays (not Saturday or Sunday) and not closed days
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const isPastDate = currentDate < today;
            // Check if date is within published range
            const isPublishedDate = getIsCellDisabled(
              publishDateRange.initialDateObj,
              publishDateRange.lastDateObj,
              currentDate
            );
            if (
              !(isClosedDay(currentDate.getDay()) || IsStoreClosedByDateForShift(getReservationSettingDetail, currentDate)) &&
              !isPastDate &&
              !isPublishedDate
            ) {
              updatedShifts.push({
                ...newShift,
                Date: moment(currentDate).format(DateTimeFormatTZ),
              });
            }
          }
          break;

        case "alternate":
          // If the selected date is not found, break
          if (selectedDateIndex === -1) break;

          // Assign shift to alternate weekdays starting from the selected date
          for (let i = selectedDateIndex; i < dates.length; i++) {
            const currentDate = new Date(dates[i]);
            if (!(isClosedDay(currentDate.getDay()) || IsStoreClosedByDateForShift(getReservationSettingDetail, currentDate))) {
              if ((i - selectedDateIndex) % 2 === 0) {
                updatedShifts.push({
                  ...newShift,
                  Date: moment(currentDate).format(DateTimeFormatTZ),
                });
              }
            }
          }
          break;

        case "weekly":
          // Assign shift to the same day of week within the date range that are not closed
          const droppedDay = new Date(date).getDay();
          // If the selected date is not found, break
          if (selectedDateIndex === -1) break;
          // Assign shift to alternate weekdays starting from the selected date
          for (let i = selectedDateIndex; i < dates.length; i++) {
            const currentDate = new Date(dates[i]);
            // Check if this date has the same day of week as the dropped shift and is not a closed day
            if (
              currentDate.getDay() === droppedDay &&
              !(isClosedDay(currentDate.getDay()) || IsStoreClosedByDateForShift(getReservationSettingDetail, currentDate))
            ) {
              updatedShifts.push({
                ...newShift,
                Date: moment(currentDate).format(DateTimeFormatTZ),
              });
            }
          }
          break;

        default:
          // 'selected' - just assign to the dropped date if it's not a closed day
          const selectedDate = new Date(date);
          if (!(isClosedDay(selectedDate.getDay()) || IsStoreClosedByDateForShift(getReservationSettingDetail, selectedDate))) {
            updatedShifts.push({
              ...newShift,
              Date: date,
            });
          }
      }

      switch (assignmentType) {
        case "daily":
        case "this_week":
          if (IsCommonDates(shifts, updatedShifts).length) {
            props.confirmPopupToggleHandler();
            let commonOldDates = IsCommonDates(shifts, updatedShifts).map(
              (shift) => moment(shift).format("YYYY-MM-DD")
            );
            setOverWriteShifts({
              NewShifts: updatedShifts,
              OldShifts: shifts
                .filter((shift) => shift.StaffId === droppedShift.staffId)
                .filter((shift) =>
                  commonOldDates.includes(
                    moment(shift.Date).format("YYYY-MM-DD")
                  )
                ),
            });
          } else {
            AddShiftScheduleService(updatedShifts);
          }
          break;

        case "alternate":
        case "weekly":
          if (IsCommonDates(shifts, updatedShifts).length > 0) {
            props.confirmPopupToggleHandler();
            let commonDates = IsCommonDates(shifts, updatedShifts).map(
              (shift) => moment(shift).format("YYYY-MM-DD")
            );
            setOverWriteShifts({
              NewShifts: updatedShifts,
              OldShifts: shifts
                .filter((shift) => shift.StaffId === droppedShift.staffId)
                .filter((shift) =>
                  commonDates.includes(moment(shift.Date).format("YYYY-MM-DD"))
                ),
            });
          } else {
            AddShiftScheduleService(updatedShifts);
          }
          break;
        case "selected":
          if (IsCommonDates(shifts, updatedShifts).length > 0) {
            props.confirmPopupToggleHandler();
            let commonDates = IsCommonDates(shifts, updatedShifts).map(
              (shift) => moment(shift).format("YYYY-MM-DD")
            );
            setOverWriteShifts({
              NewShifts: updatedShifts,
              OldShifts: shifts
                .filter((shift) => shift.StaffId === droppedShift.staffId)
                .filter((shift) =>
                  commonDates.includes(moment(shift.Date).format("YYYY-MM-DD"))
                ),
            });
          } else {
            AddShiftScheduleService(updatedShifts);
          }
      }
      setIsAssignmentModalOpen(false);
      setDroppedShift(null);
      setAssignmentType("selected");
    }
  };

  const today = new Date();
  const todayRef = useRef<HTMLTableCellElement>(null);
  const nextMonhRef = useRef<HTMLTableCellElement>(null);

  const [cellWidth, setCellWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const container = document.querySelector(".table-scrollable") as HTMLElement | null;
    if (!container) return;

    const updateCellWidth = () => {
      const totalWidth = container.offsetWidth;
      const isSmallScreen = window.innerWidth < 1024;
      const fixedColWidth = isSmallScreen
        ? allocation === 3
          ? 60
          : 100
        : allocation === 3
        ? 130
        : 200;
      //const fixedColWidth = allocation === 3 ? 130 : 200;
      // Available width for date columns
      const availableWidth = totalWidth - fixedColWidth;
      
      const userAgent = navigator.userAgent.toLowerCase();
      let adjustment = 1.1; // default (for Firefox)
      if (userAgent.includes("chrome") && !userAgent.includes("edge")) {
        adjustment = 0.7; // Chrome/WebKit
      } else if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
        adjustment = 0.8; // Safari
      }
      let visibleDays = 7; // weekly default
      if (allocation === 2) visibleDays = 14;
      else if (allocation === 3) visibleDays = moment(currentDate).daysInMonth();

      // compute per-day width (leave slight padding margin)
      const widthPerDay = availableWidth / visibleDays - adjustment;
      setCellWidth(widthPerDay);
    };

    updateCellWidth();

    // update on resize and orientation change
    window.addEventListener("resize", updateCellWidth);
    window.addEventListener("orientationchange", updateCellWidth);

    return () => {
      window.removeEventListener("resize", updateCellWidth);
      window.removeEventListener("orientationchange", updateCellWidth);
    };
  }, [dates, currentAllocationClassName]);



  // Scroll to today's date (with 200px offset from start)
  useLayoutEffect(() => {
    if (todayRef.current) {
      const container = todayRef.current.closest(".table-scrollable") as HTMLElement | null;
      if (container) {
        // ✅ Wait for DOM reflow (after widths change)
        requestAnimationFrame(() => {
          const containerRect = container.getBoundingClientRect();
          const targetRect = todayRef.current!.getBoundingClientRect();

          const offsetLeft = targetRect.left - containerRect.left;
          const isSmallScreen = window.innerWidth < 1024;
          const fixedColWidth = isSmallScreen
            ? 100
            : 200;
          let targetScroll = container.scrollLeft + offsetLeft - fixedColWidth;
          if (targetScroll < 0) targetScroll = 0;

          container.scrollTo({
            left: targetScroll,
            behavior: "smooth",
          });

          // ✅ Force Safari to repaint after scroll
          requestAnimationFrame(() => {
            container.style.webkitTransform = "translateZ(0)";
            void container.offsetHeight; // trigger reflow
            container.style.webkitTransform = "";
          });
        });
      }
    }
  }, [dates, currentAllocationClassName]);

  
    // Scroll to next month start (center align)
  useLayoutEffect(() => {
    if (nextMonhRef.current) {
      const container = nextMonhRef.current.closest(".table-scrollable");
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const targetRect = nextMonhRef.current.getBoundingClientRect();

        const offsetLeft =
          targetRect.left - containerRect.left - container.clientWidth / 2 + targetRect.width / 2;

        container.scrollTo({
          left: container.scrollLeft + offsetLeft,
          behavior: "smooth",
        });
      }
    }
  }, [dates, currentAllocationClassName]);

  // Helper to check start of next month
  const isNextMonthStartDate = (input: string | Date | moment.Moment): boolean => {
    const today = moment();
    const givenDate = moment(input);

    if (today.month() === givenDate.month() && today.year() === givenDate.year()) return false;
    if (givenDate.date() !== 1) return false;
    return true;
  };

  const checkIfPublishDateHaveExtraDates = (
    range:
      | { firstDate: null; lastDate: null }
      | { firstDate: string; lastDate: string },
    publishedDateRange: { StartDate: string; EndDate: string }
    ) => {
    if (!range.firstDate || !range.lastDate) return false;
    if (!publishedDateRange.StartDate || !publishedDateRange.EndDate) return false;
    const actualStart = new Date(range.firstDate);
    const actualEnd = new Date(range.lastDate);

    const publishStart = new Date(publishedDateRange.StartDate);
    const publishEnd = new Date(publishedDateRange.EndDate);

    if (publishStart < actualStart || publishEnd > actualEnd) {
      return true;
    }
    return false;
    };

  

  return (
    <>
      {props.showDeletePopup && (
        <DeletePopup
          modalSubmit={removeShiftHandler}
          modalClosed={() => props.deletePopupToggleHandler()}
          isLoading={deleteShiftScheduleState.inProgress}
          title="delete_confirmation"
          message="are_you_sure_you_want_to_remove_this_shift?"
        />
      )}
      {props.showConfirmPopup && (
        <ConfirmPopup
          modalSubmit={overWriteShiftHandler}
          modalClosed={() => props.confirmPopupToggleHandler()}
          isLoading={overWriteShiftScheduleListState.inProgress}
          title="overwrite_schedule_confirmation"
          message="are_you_sure_you_want_to_overwrite_the_schedule"
        />
      )}
      {props.showConfirmEmailPopup && (
        <ConfirmPopup
          modalSubmit={onSubmitUnPublishHandler}
          modalClosed={() => props.confirmEmailPopupToggleHandler()}
          isLoading={false}
          title="confirmation_popup_title"
          message="unpublish_schedule_confirmation"
        />
      )}
      {showShowPopup && (
        <ConfirmPopup
          modalSubmit={handlePublish}
          modalClosed={() => showPopupToggleHandler()}
          isLoading={false}
          title="confirmation_popup_title"
          message="publish_unpublish_schedule_confirmation"
        />
      )}
      {props.showPublishSchedulePopup && (
        <PublishForm
          submitHandler={handlePublishConfirmPopupHandler}
          onModalClosed={() => {
            props.publishSchedulePopupToggleHandler();
          }}
          actionInProgress={false}
          initialValues={submitPublishValues ? submitPublishValues : {
            // StartDate: dates[0],
            StartDate: new Date(),
            EndDate: dates[dates.length - 1],
          }}
          storeId={storeId}
          selectedOption={selectedOption}
          publishDateRange={{
            startDate: publishDateRange.initialDateObj,
            endDate: publishDateRange.lastDateObj,
          }}
          dateSchedules={dates}
        />
      )}
      {props.showChangeShiftPopup && (
        <ChangeShiftForm
          submitHandler={handleShiftChange}
          onModalClosed={() => {
            props.changeShiftPopupToggleHandler();
          }}
          actionInProgress={false}
          initialValues={{
            Shift: changeShiftId,
          }}
          storeId={storeId}
          shiftOptions={allShiftOption}
        />
      )}
      {props.showFreeShiftPopup && (
        <FreeShiftForm
          submitHandler={handleFreeTimeShift}
          onModalClosed={() => {
            props.freeShiftPopupToggleHandler();
          }}
          actionInProgress={false}
          initialValues={{
            FreeStartTime: "00:00",
            FreeEndTime: "00:00",
          }}
          storeId={storeId}
        />
      )}
      <FormLoader loading={getShiftScheduleListState.inProgress} />
      <div className="schedule_table_header"> 
        <div>
          <DropdownInput
            className="form-control"
            value={allocation}
            onChange={handleAllocationChange}
            options={allocationList.map((option) => ({
              label: option.value,
              value: option.id,
            }))}
          />
        </div>

        <div className="d-flex g-1 align-items-center">
          <IconButton onClick={() => handleMonthChange(-1)}>{"<"}</IconButton>
          <Typography variant="h6"> {year}
            <FormattedMessage id="label_year" />{" "}
            {formatMessage(monthName)}
          </Typography>
          <IconButton onClick={() => handleMonthChange(1)}>{">"}</IconButton>
        </div>
        <div className="">
          <small className="py-1 px-3 text-white rounded-pill bg-secondary">{`${
            publishDateRange.initialDate > 0 && publishDateRange.lastDate > 0
              ? ` ${formatMessage("published")}  ${
                  publishDateRange.initialDate
                }-${publishDateRange.lastDate}`
              : formatMessage("unpublished")
          }`}</small>
          {
            !isDisabled ? (
              <Button
                className="ms-2"
                variant="primary"
                onClick={handleMenuClick}
              >
                <FormattedMessage id="actions" />
              </Button>
            ) : (
              <div></div>
            ) // this blank division is created to balance calendar placements. DO NOT REMOVE!!!
          }
        </div>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem
            onClick={() => {
              handleOptionSelect(1);
              props.publishSchedulePopupToggleHandler();
            }}
          >
            {formatMessage("publish")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleOptionSelect(2);
              props.confirmEmailPopupToggleHandler();
            }}
            disabled={!(publishDateRange.initialDate > 0 && publishDateRange.lastDate > 0)}
          >
            {formatMessage("unpublish")}
          </MenuItem>
          <MenuItem onClick={() => handleCopySchedule()}>
            {formatMessage("copy_previous_month")}
          </MenuItem>
        </Menu>
      </div>

      {/* Table */}
      <div className={`table-scrollable ${currentAllocationClassName}`}>
        <table className="schedule_table">
          <thead>
            <tr>
              <th>
                <p className="schedule_table_header_title">
                  {formatMessage("staff")}
                </p>
              </th>
              {dates.map((date) => (
                <th
                  key={date.toISOString()}
                  ref={
                    isSameDay(date, today)
                      ? todayRef
                      : isNextMonthStartDate(date)
                      ? nextMonhRef
                      : null
                  }
                  style={{
                    minWidth: `${cellWidth}px`,
                    maxWidth: `${cellWidth}px`,
                    width: `${cellWidth}px`,
                  }}
                >
                  <StyledHeaderCell key={date.toISOString()}>
                    <h3 className="schedule_table_date">{date.getDate()}</h3>
                    <p className="schedule_table_day">
                      {formatMessage(getJapaneseDay(date))}
                    </p>
                  </StyledHeaderCell>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {getAllShiftStaffOptions.map((staff) => (
              <tr key={staff.id}>
                <td>
                  <StyledStaffCell>
                    <Avatar
                      src={
                        staff?.data?.Image ? staff?.data?.Image : demoStoreIcon
                      }
                      sx={{ width: 35, height: 35 }}
                    ></Avatar>
                    <div>
                      <Typography fontWeight="medium">{staff.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {staff?.data ? staff?.data?.Expertise : ""}
                      </Typography>
                    </div>
                  </StyledStaffCell>
                </td>
                {dates.map((date) => (
                  <td
                    key={`${staff.id}-${date.toISOString()}`}
                    style={{
                      minWidth: `${cellWidth}px`,
                      maxWidth: `${cellWidth}px`,
                      width: `${cellWidth}px`,
                    }}
                  >
                    <StyledCell
                      key={`${staff.id}-${date.toISOString()}`}
                      isOver={false}
                      disabled={isCellDisabled(
                        date,
                        publishDateRange.initialDateObj,
                        publishDateRange.lastDateObj
                      )}
                    >
                      <ScheduleCell
                        staffId={staff.id}
                        date={date}
                        shift={getShiftForCell(staff.id, date)}
                        onDrop={
                          isDisabled
                            ? () => {}
                            : (item: IShiftSchedule) => {
                                if (
                                  !isCellDisabled(
                                    date,
                                    publishDateRange.initialDateObj,
                                    publishDateRange.lastDateObj
                                  )
                                ) {
                                  const existingShift = getShiftForCell(
                                    staff.id,
                                    date
                                  );
                                  if (
                                    shifts.filter(
                                      (shift) => shift.StaffId === item.StaffId
                                    ).length > 0
                                  ) {
                                  }
                                  // Add check for existing shift
                                  setDroppedShift({
                                    shift: item,
                                    staffId: staff.id,
                                    date: date.toISOString(),
                                    existingShift: existingShift,
                                  });
                                  setIsAssignmentModalOpen(true);
                                }
                              }
                        }
                        onEdit={handleEditShift}
                        onRemove={handleDeleteShift}
                        isDisabled={isCellDisabled(
                          date,
                          publishDateRange.initialDateObj,
                          publishDateRange.lastDateObj
                        )}
                        isPublisedDate={getIsCellDisabled(
                          publishDateRange.initialDateObj,
                          publishDateRange.lastDateObj,
                          date
                        )}
                        onCellClick={isDisabled ? () => {} : handleCellClick}
                        onChange={handleChangeShift}
                        isPermissionViewOnly={isDisabled}
                        getClosedDay={getClosedDay}
                      />
                    </StyledCell>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal show={isAssignmentModalOpen} size="sm" position="center">
        <Modal.Header>
          <Modal.Title>
            {formatMessage("assign_shift")}
            <button
              onClick={() => setIsAssignmentModalOpen(false)}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Check
                type="radio"
                id="selected"
                name="assignmentType"
                value="selected"
                label={formatMessage("selected_day_only")}
                checked={assignmentType === "selected"}
                onChange={(e) => setAssignmentType(e.target.value)}
              />
              <Form.Check
                type="radio"
                id="daily"
                name="assignmentType"
                value="daily"
                label={formatMessage("daily_assign_shift_dropdown")}
                checked={assignmentType === "daily"}
                onChange={(e) => setAssignmentType(e.target.value)}
              />
              <Form.Check
                type="radio"
                id="alternate"
                name="assignmentType"
                value="alternate"
                label={formatMessage("alternate_days")}
                checked={assignmentType === "alternate"}
                onChange={(e) => setAssignmentType(e.target.value)}
              />
              <Form.Check
                type="radio"
                id="weekly"
                name="assignmentType"
                value="weekly"
                label={formatMessage("weekly_assign_shift_dropdown")}
                checked={assignmentType === "weekly"}
                onChange={(e) => setAssignmentType(e.target.value)}
              />
              <Form.Check
                type="radio"
                id="this_week"
                name="assignmentType"
                value="this_week"
                label={formatMessage("this_week_assign_shift_dropdown")}
                checked={assignmentType === "this_week"}
                onChange={(e) => setAssignmentType(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-danger"
            onClick={() => setIsAssignmentModalOpen(false)}
          >
            <i className="bi bi-x-lg me-1"></i>
            {formatMessage("button_cancel")}
          </Button>
          <Button variant="primary" onClick={handleAssignmentConfirm}>
            <i className="bi bi-check-lg me-1"></i>
            {formatMessage("button_confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

interface FreeShiftMenuDropdownProps {
  onRemove?: () => void;
  shiftId: number;
  date: Date;
  shift: IShiftSchedule;
}

const FreeShiftMenuDropdown: React.FC<FreeShiftMenuDropdownProps> = (props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <div
        onClick={handleClick}
        className="p-0"
        aria-controls={anchorEl ? "shift-menu" : undefined}
        aria-haspopup="true"
      >
        {/* Add a clickable icon */}
        <IconButton size="small">
          <svg
            id="fi_2311524"
            fill="currentColor"
            enableBackground="new 0 0 32 32"
            height="16"
            viewBox="0 0 32 32"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              id="XMLID_294_"
              d="m13 16c0 1.654 1.346 3 3 3s3-1.346 3-3-1.346-3-3-3-3 1.346-3 3z"
            ></path>
            <path
              id="XMLID_295_"
              d="m13 26c0 1.654 1.346 3 3 3s3-1.346 3-3-1.346-3-3-3-3 1.346-3 3z"
            ></path>
            <path
              id="XMLID_297_"
              d="m13 6c0 1.654 1.346 3 3 3s3-1.346 3-3-1.346-3-3-3-3 1.346-3 3z"
            ></path>
          </svg>
        </IconButton>
      </div>

      <Menu
        id="shift-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {props.onRemove && (
          <MenuItem
            onClick={() => {
              props.onRemove && props.onRemove();
              handleClose();
            }}
          >
            <FormattedMessage id="remove" />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default ConfirmEmailPopupHOC(
  ShowPopupHOC(
    ChangeShiftPopupHOC(
      FreeShiftPopupHOC(
        ConfirmPopupHOC(
          DeletePopupHOC(PublishSchedulePopupHOC(observer(ScheduleGrid)))
        )
      )
    )
  )
);